// src/pages/hr/HRDashboard.jsx
import React, { useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useMyTasks,
  useUpcomingMeetings,
  useEmployeeAnnouncements,
  useMonthlyAttendance,
  useMyLeaveRequests,
} from "../../hooks/useFirestore";
import { HR_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import AttendanceCard from "../../components/attendance/AttendanceCard";
import { useNotes } from "../../context/NotesContext";
import { isEmployeeVisible, formatTimeStr } from "../../utils/meetings";
import { useUpcomingHolidays, useEmployeeShift, useHolidaysInMonth } from "../../hooks/useFirestoreExtensions";
import { calculateEmployeeStatsForRange } from "../../utils/statusCalculations";

function getShiftDuration(start, end) {
  if (!start || !end) return "";
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 1440; // overnight
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m ? ` ${m}m` : ""}`;
}


// ════════════════════════════════════════════════════════════
export default function HRDashboard() {
  const { userProfile } = useAuth();
  const { notes } = useNotes();
  const uid = userProfile?.uid || userProfile?.id;

  const { data: tasks } = useMyTasks(uid);
  const { data: meetings } = useUpcomingMeetings();
  const todayMeetings = (meetings || []).filter((m) => isEmployeeVisible(m, userProfile));

  const { data: announcements } = useEmployeeAnnouncements(userProfile?.teamId);

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const { data: monthAtt } = useMonthlyAttendance(uid, year, month);
  const { data: monthHolidays } = useHolidaysInMonth(year, month);
  const { data: myLeaves } = useMyLeaveRequests(uid);

  const monthlyStats = useMemo(() => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const monthStr = String(month).padStart(2, "0");
    const startDate = `${year}-${monthStr}-01`;
    const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;
    const lastDay = isCurrentMonth
      ? today.getDate()
      : new Date(year, month, 0).getDate();
    const endDate = `${year}-${monthStr}-${String(lastDay).padStart(2, "0")}`;

    return calculateEmployeeStatsForRange({
      attendanceRecords: monthAtt,
      leaveRequests: myLeaves,
      holidays: monthHolidays,
      startDate,
      endDate,
      todayStr
    });
  }, [monthAtt, myLeaves, monthHolidays, year, month]);

  const presentDays = monthlyStats.present;
  const attendRate = monthlyStats.rate;

  const pendingTasks = tasks.filter(t => t.status !== "COMPLETED").length;
  const completedTasks = tasks.filter(t => t.status === "COMPLETED").length;

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const pendingLeavesCount = myLeaves.filter(r => r.status === "pending_lead" || r.status === "pending_admin").length;
  const balance = userProfile?.leaveBalance || {};
  const totalBalance = (balance.casual || 0) + (balance.sick || 0) + (balance.annual || 0);

  const { data: upcomingHolidays } = useUpcomingHolidays();
  const { data: myShift } = useEmployeeShift(uid);

  const pendingReminders = notes.filter(n =>
    n.category === "reminder" &&
    n.reminderDate &&
    new Date(n.reminderDate) >= new Date(new Date().setHours(0, 0, 0, 0))
  ).sort((a, b) => new Date(a.reminderDate) - new Date(b.reminderDate));

  return (
    <Layout navItems={HR_NAV} panelLabel="HR Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Dashboard</h1>
          <p className="page-subtitle">{today}</p>
        </div>
        <div className="user-greeting">
          <span>Hello, {userProfile?.displayName?.split(" ")[0]} </span>
        </div>
      </div>

      {/* ── KPI row ─────────────────────────── */}
      <div className="kpi-grid">

        <div className="kpi-card kpi-card--blue">
          <div className="kpi-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              width="24"
              height="24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
              />
            </svg>
          </div>

          <div className="kpi-body">
            <span className="kpi-value">{presentDays}</span>
            <span className="kpi-label">Days Present</span>
            <span className="kpi-sub">This month</span>
          </div>
        </div>

        <div className="kpi-card kpi-card--green">
          <div className="kpi-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              width="24"
              height="24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
              />
            </svg>
          </div>

          <div className="kpi-body">
            <span className="kpi-value">{attendRate}%</span>
            <span className="kpi-label">Attendance Rate</span>
            <span className="kpi-sub">Month average</span>
          </div>
        </div>

        <div className="kpi-card kpi-card--amber">
          <div className="kpi-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              width="24"
              height="24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
          </div>

          <div className="kpi-body">
            <span className="kpi-value">{pendingTasks}</span>
            <span className="kpi-label">Pending Tasks</span>
            <span className="kpi-sub">{completedTasks} completed</span>
          </div>
        </div>

        <div className="kpi-card kpi-card--purple">
          <div className="kpi-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              width="24"
              height="24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
              />
            </svg>
          </div>

          <div className="kpi-body">
            <span className="kpi-value">{totalBalance}</span>
            <span className="kpi-label">Leave Balance</span>
            <span className="kpi-sub">Total days available</span>
          </div>
        </div>

        <div className="kpi-card kpi-card--blue">
          <div className="kpi-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              width="24"
              height="24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
          </div>

          <div className="kpi-body">
            <span className="kpi-value">{pendingLeavesCount}</span>
            <span className="kpi-label">Pending Leaves</span>
            <span className="kpi-sub">Awaiting approval</span>
          </div>
        </div>

      </div>

      {/* ── Internship Progress (interns only) ─── */}
      {(userProfile?.employmentType === "paid-intern" || userProfile?.employmentType === "unpaid-intern" || userProfile?.employmentType === "intern") && (() => {
        const startStr = userProfile.internshipStartDate || userProfile.joiningDate || null;
        const start = startStr ? new Date(startStr) : null;
        const end = userProfile.internshipEndDate ? new Date(userProfile.internshipEndDate) : null;
        const now = new Date();
        const duration = userProfile.internshipDuration || 3;
        const totalDays = start && end ? Math.ceil((end - start) / 86400000) : (duration === 3 ? 90 : duration * 30);
        const elapsed = start ? Math.ceil((now - start) / 86400000) : 0;
        const remaining = end ? Math.max(0, Math.ceil((end - now) / 86400000)) : Math.max(0, totalDays - elapsed);
        const pct = totalDays > 0 ? Math.min(100, Math.round((elapsed / totalDays) * 100)) : 0;
        const remainLabel = remaining <= 0 ? "Completed" : `${remaining} Days Left`;
        const status = userProfile.internshipStatus === "converted" ? "Converted to Full-Time"
          : remaining <= 0 ? "Completed"
            : remaining <= 15 ? "Completing Soon"
              : "Active";
        return (
          <div className="dashboard-card" style={{ marginBottom: "0.5rem" }}>
            <div className="card-header">
              <h3
                className="card-title"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  width="22"
                  height="22"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                  />
                </svg>

                Internship Progress
              </h3>
              <span className={`status-pill status-pill--${status === "Active" ? "green" : status === "Completing Soon" ? "amber" : "blue"
                }`} style={{ fontSize: "0.65rem" }}>{status}</span>
            </div>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginTop: "0.5rem" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "var(--brand-accent)" }}>{pct}%</div>
                <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{remainLabel}</div>
              </div>
              <div style={{ flex: 2 }}>
                <div className="att-progress-wrap" style={{ height: "8px" }}>
                  <div className="att-progress-bar" style={{ width: `${pct}%` }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.65rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                  <span>{startStr || "—"}</span>
                  <span>{userProfile.internshipEndDate || "—"}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="dashboard-grid">
        {/* ── Attendance widget (Shared Component) ── */}
        <AttendanceCard userId={uid} userProfile={userProfile} />

        {/* ── My Shift ──────────────────────── */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">My Shift</h3>
          </div>
          {myShift ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "0.5rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <div className="status-pill status-pill--blue" style={{ padding: "0.75rem", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="24" height="24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--text-primary)" }}>
                    {myShift.shiftName}
                  </div>
                  <span className="status-pill status-pill--green" style={{ marginTop: "0.35rem", fontSize: "0.7rem", display: "inline-block" }}>
                    {myShift.status || "Active"}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: "2rem", borderTop: "1px solid var(--border)", paddingTop: "0.75rem" }}>
                <div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Start</div>
                  <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "var(--text-primary)", marginTop: "0.1rem" }}>
                    {formatTimeStr(myShift.shiftStart) || "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>End</div>
                  <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "var(--text-primary)", marginTop: "0.1rem" }}>
                    {formatTimeStr(myShift.shiftEnd) || "—"}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Duration</div>
                  <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "var(--text-primary)", marginTop: "0.1rem" }}>
                    {getShiftDuration(myShift.shiftStart, myShift.shiftEnd) || "—"}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card-empty">No shift assigned. Contact your Admin.</div>
          )}
        </div>

        {/* ── My tasks ──────────────────────── */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">My Tasks</h3>
            <a href="/employee/tasks" className="card-link">View all →</a>
          </div>
          {tasks.length === 0 ? (
            <div className="card-empty">No tasks assigned.</div>
          ) : (
            <div className="task-list">
              {tasks.slice(0, 4).map(t => (
                <div key={t.id} className="task-row">
                  <div className="task-row-info">
                    <span className="task-row-title">{t.title}</span>
                    <span className="task-row-meta">
                      by {t.assignedByName} · due{" "}
                      {t.deadline?.toDate?.()?.toLocaleDateString?.("en-IN") || "—"}
                    </span>
                  </div>
                  <span className={`status-pill status-pill--${t.status === "COMPLETED" ? "green" : t.status === "IN_PROGRESS" ? "blue" : "gray"
                    }`}>
                    {t.status.replace("_", " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Today's meetings ─────────────── */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Today's Meetings</h3>
            <span className="card-badge">{todayMeetings.length}</span>
          </div>
          {todayMeetings.length === 0 ? (
            <div className="card-empty">No more meetings today.</div>
          ) : (
            <div className="meetings-list">
              {todayMeetings.slice(0, 4).map(m => (
                <div key={m.id} className="meeting-row">
                  <div className="meeting-dot" />
                  <div className="meeting-info">
                    <span className="meeting-title">{m.title}</span>
                    <span className="meeting-time">{m.date} · {formatTimeStr(m.startTime || m.time)}{m.endTime && ` - ${formatTimeStr(m.endTime)}`}</span>
                  </div>
                  {m.meetLink && (() => {
                    const now = new Date();
                    const start = new Date(`${m.date}T${m.startTime || m.time}`);
                    const joinWindow = new Date(start.getTime() - 10 * 60 * 1000);
                    return now >= joinWindow ? (
                      <a href={m.meetLink} target="_blank" rel="noreferrer" className="meeting-join-btn">
                        Join
                      </a>
                    ) : (
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{formatTimeStr(m.startTime || m.time)}</span>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Announcements ─────────────────── */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Announcements</h3>
          </div>
          {announcements.length === 0 ? (
            <div className="card-empty">No announcements.</div>
          ) : (
            <div className="announcements-list">
              {announcements.slice(0, 3).map(a => (
                <div key={a.id} className={`announcement-row priority-${a.priority}`}>
                  <span className="announcement-priority-dot" />
                  <div className="announcement-content">
                    <span className="announcement-title">{a.title}</span>
                    <span className="announcement-msg">{a.message}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Pending Reminders ─────────────────── */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Pending Reminders</h3>
            <span className="card-badge">{pendingReminders.length}</span>
          </div>
          {pendingReminders.length === 0 ? (
            <div className="card-empty">No pending reminders.</div>
          ) : (
            <div className="announcements-list">
              {pendingReminders.slice(0, 3).map(r => (
                <div key={r.id} className="announcement-row">
                  <span className="announcement-priority-dot" style={{ backgroundColor: "#f59e0b" }} />
                  <div className="announcement-content">
                    <span className="announcement-title">{r.title}</span>
                    <span className="announcement-msg" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        marginRight: '0.5rem'
                      }}>{r.content}</span>
                      <span style={{ fontWeight: 'bold', color: '#b45309', whiteSpace: 'nowrap' }}>
                        {new Date(r.reminderDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>


        {/* ── Upcoming Holidays ───────────── */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Upcoming Holidays</h3>
            <span className="card-badge">{upcomingHolidays.length}</span>
          </div>
          {upcomingHolidays.length === 0 ? (
            <div className="card-empty">No upcoming holidays scheduled.</div>
          ) : (
            <div className="announcements-list">
              {upcomingHolidays.slice(0, 4).map((h) => {
                const dt = new Date(h.date + "T12:00:00");
                const daysLeft = Math.ceil((dt - new Date()) / 86400000);
                const typeColor = h.type === "public" ? "green" : h.type === "optional" ? "blue" : "amber";
                return (
                  <div key={h.id} className="announcement-row">
                    <span className="announcement-priority-dot" />
                    <div className="announcement-content">
                      <span className="announcement-title">🎉 {h.name}</span>
                      <span className="announcement-msg">
                        {dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        {" · "}
                        <span className={`status-pill status-pill--${typeColor}`} style={{ fontSize: "0.65rem" }}>{h.type}</span>
                        {" · "}
                        {daysLeft === 1 ? "Tomorrow!" : `in ${daysLeft} days`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
